import styled, { css } from "styled-components"
import { Link } from "gatsby"
import { GatsbyImage } from "gatsby-plugin-image"
import Div from "../components/div/div.component"

export const BlogPostContent = styled.div`
  /*avoid class name conflicts*/

  .custom-container {
    position: relative;
    width: 100%;
    padding-bottom: 56.25%;
    height: 0;

    iframe {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
    }
  }

  /* make sure GIF video tag is responsive and remove black video outline */
  video {
    display: block;
    margin: 0 auto;
    width: 90%;
    height: auto;
    ::-webkit-media-controls-enclosure {
      border: 1px solid #fff;
    }
  }

  a {
    color: ${({ theme: { colors } }) => colors.accentBlue};
  }
  /* adding margin around blog post images so that we don't need to add line break */
  img {
    width: 100%;
    height: auto;
    margin: 55px 0;
    @media screen and (max-width: 760px) {
      margin: 35px 0;
    }
  }
`
export const BlogPostHero = styled(GatsbyImage)`
  width: 100%;
  margin-bottom: 50px;
  @media screen and (max-width: 760px) {
    margin-bottom: 30px;
  }
`

BlogPostHero.displayName = "BlogPostHero"

const BlogPostOverides = css`
  h1,
  h2,
  h3,
  h4 {
    margin: 60px 0 30px;
    scroll-margin-top: 90px;
  }
  p {
    margin: 4px 0 20px 0;
  }
  h1 {
    ${({ theme: { typography } }) => typography.desktop.h1};
  }
  h2 {
    ${({ theme: { typography } }) => typography.desktop.h2};
  }
  h3 {
    ${({ theme: { typography } }) => typography.desktop.h3};
  }
  h4 {
    ${({ theme: { typography } }) => typography.desktop.h4};
  }

  li {
    line-height: 1.8em;
    margin-bottom: 5px;
    &::marker {
      color: ${({ theme: { colors } }) => colors.accentBlue};
      font-weight: 700;
    }
  }

  ol,
  ul {
    /* To align the list with the text */
    padding-left: 25px;
  }

  blockquote {
    border-left: 3px solid ${({ theme: { colors } }) => colors.accentBlue};
    margin-left: 3px;
    p {
      color: ${({ theme: { colors } }) => colors.accentBlue};
    }
  }

  /* Styles the code block in a blog post */
  pre {
    background: rgba(19, 15, 235, 0.03);
    /* box-shadow: 2px 4px 40px rgba(19, 15, 235, 0.1); */
    color: #2e2e2e;
    font-family: Courier New, Courier, monospace;
    padding: 15px 30px;
    border-radius: 5px;
    overflow-x: scroll;
  }
  pre code {
    font-family: Courier New, Courier, monospace;
  }
  @media screen and (max-width: 760px) {
    h1 {
      ${({ theme: { typography } }) => typography.mobile.h1};
    }
    h2 {
      ${({ theme: { typography } }) => typography.mobile.h2};
    }
    h3 {
      ${({ theme: { typography } }) => typography.mobile.h3};
    }
    h4 {
      ${({ theme: { typography } }) => typography.mobile.h4};
    }
    h1,
    h2,
    h3,
    h4 {
      margin-bottom: 15px;
    }
  }
`
// email CTA has to overwrite styling defined in the container below
export const BlogPostBodyContainer = styled.div`
  grid-column: 4/10;
  font-size: 20px;
  margin-bottom: 40px;

  @media screen and (max-width: 1250px) {
    grid-column: 3/11;
  }

  @media screen and (max-width: 1000px) {
    grid-column: 2/6;
  }

  @media screen and (max-width: 760px) {
    width: 100%;
    grid-column: 1/-1;
    margin: 0 auto;
  }
  ${BlogPostOverides}
`

BlogPostBodyContainer.displayName = "BlogPostBodyContainer"

export const BlogPostHeaderContainer = styled.div`
  grid-column: 4/10;
  margin-bottom: 60px;
  @media screen and (max-width: 1250px) {
    grid-column: 3/11;
  }
  @media screen and (max-width: 1000px) {
    grid-column: 2/6;
  }
  @media screen and (max-width: 760px) {
    grid-column: 1/-1;
  }
`

BlogPostHeaderContainer.displayName = "BlogPostHeaderContainer"

export const BlogPostTitle = styled.h1`
  ${({ theme: { typography } }) => typography.desktop.h1}
  @media screen and (max-width: 760px) {
    ${({ theme: { typography } }) => typography.mobile.h1}
  }
`

BlogPostTitle.displayName = "BlogPostTitle"

export const BlogPostAuthorName = styled.span`
  grid-area: author-name;
  font-size: 16px;
`

BlogPostAuthorName.displayName = "BlogPostAuthorName"

export const SocialContainer = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: flex-start;
  margin: 0;
  & div {
    margin-right: 10px;
  }
`

export const DividerLine = styled.div`
  width: 25px;
  height: 3px;
  border-radius: 50px;
  background: ${({ theme: { colors } }) => colors.accentBlue};
  margin-top: 40px;
  margin-bottom: 60px;

  @media screen and (max-width: 760px) {
    margin-bottom: 40px;
  }
`

export const AuthorContainer = styled.div`
  display: flex;
  width: 100%;
`

export const AuthorAvatar = styled.img`
  height: 75px;
  width: auto;
  border-radius: 50%;
  margin-right: 15px;
  box-shadow: 2px 4px 40px rgba(76, 15, 251, 0.1);
`

export const AuthorName = styled.h5`
  ${({ theme: { typography } }) => typography.desktop.h5}
  margin: 0 !important;
`

export const AuthorSeperatorDot = styled.div`
  width: 3px;
  height: 3px;
  background: rgb(76, 15, 251);
  border-radius: 50%;
  margin: auto 8px;
`

export const PostMeta = styled.div`
  margin: 10px 0;
`

export const PostMetaFlex = styled.div`
  display: flex;
  margin-top: ${(props) => (props.mt ? props.mt : "none")};
`

export const PostMetaContent = styled.span`
  ${({ theme: { typography } }) => typography.desktop.metadata}
  color: ${({ theme: { colors } }) => colors.grey};
`

export const TagsContainer = styled.div`
  display: flex;
  flex-direction: row;
  margin-bottom: 40px;

  & a {
    text-decoration: none;
    margin-right: 6px;
  }

  @media screen and (max-width: 760px) {
    margin-bottom: 25px;
  }
`

export const ArticleTag = styled(Link)`
  display: flex;
  background-color: #f6f6fb;
  border: 1px solid #f6f6fb;
  border-radius: 15px;
  padding: 0 15px;
  height: 25px;
  font-size: 12px;
  font-weight: 600;
  color: #4c0ffb;
  cursor: pointer;

  &:hover,
  :focus {
    border: 1px solid #4c0ffb;
  }

  & span {
    margin: auto;
  }

  svg {
    margin-right: 2px;
  }
`

ArticleTag.displayName = "ArticleTag"

export const RelatedPostContainer = styled(Div)`
  background-color: ${({ theme: { colors } }) => colors.lightGrey};
  padding-bottom: 85px;
  margin-top: 60px;
  @media screen and (max-width: 1440px) {
    padding-left: 50px;
    padding-right: 50px;
  }
  @media screen and (max-width: 1115px) {
    padding-left: 20px;
    padding-right: 20px;
  }
  @media screen and (max-width: 760px) {
    padding-bottom: 50px;
    margin-top: 40px;
  }
`

// TO DO HERE - follow theme js h2 styling
export const RelatedTitle = styled.h2`
  grid-column: 1/-1;
  ${({ theme: { typography } }) => typography.desktop.h2}
  text-align: center;
  margin: 100px 0 50px 0;

  @media screen and (max-width: 760px) {
    ${({ theme: { typography } }) => typography.mobile.h2}
    margin: 60px 0 30px 0;
    text-align: left;
  }
`

export const LinkCardContainer = styled.div`
  margin-top: 60px;
`

export const RelatedPostGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(275px, 3fr));
  column-gap: 30px;
  max-width: 1340px;
  margin: 0 auto;

  @media screen and (max-width: 1000px) {
    grid-template-columns: 1fr 1fr;
  }
  @media screen and (max-width: 760px) {
    grid-template-columns: 1fr;
  }
`
